import React, { useState, useEffect } from "react";

import styled from "styled-components";
import moment from "moment";
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

// Function //
import { useLanguage } from "../../Context/LanguageContext";
import { makeUpWorkout } from "../../Exercise/Transaction/Transaction.js";
import FetchModule from "../../Model/Share/Network/FetchModule";
// Function //

// Component //
import CalendarBoxButton from "../Component/CalendarBoxButton";
import WorkoutSelectBox from "../Component/WorkoutSelectBox";
import MainPageWodViewBox from "../Model/WodViewBox.js";
import MainPageRankingView from "../Model/MainPageRankingView.js";
// Component //

// Image //
import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';
import WhiteCrownImage from '../../image/WhiteCrown.png';
import { ReactComponent as EmptySignSvg } from '../../image/EmptySign.svg';
import { ReactComponent as BlackBoardModeIconSvg } from '../../image/BlackBoardMode.svg';
// Image //

export default function BoardPage() {
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const [date, setDate] = useState(moment());
  const [wods, setWods] = useState([]);
  const [selectOptions, setSelectOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(undefined);
  const [selectedWod, setSelectedWod] = useState(undefined);
  const [isClickedRanking, setIsClickedRanking] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isHoverBlackboardButton, setIsHoverBlackboardButton] = useState(false);

  const [nowSection, setNowSection] = useState(0);
  const [nowScaleSection, setNowScaleSection] = useState(undefined);

  // 금일 와드 정보 불러오기 //
  useEffect(() => {
    setIsLoading(true);

    let requestData = {
      date: date.format('YYYY-MM-DDT00:00:00')
    }

    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataOfExerciseVersion2('workout/daily_wod', 'POST', requestData);
        if (responseData.status === 200) {
          if (responseData.data.workouts.length > 0) {
            let newWorkouts = makeUpWorkout(responseData.data.workouts.slice());
            let newSelectOptions = [];

            for (let i = 0; i < newWorkouts.length; i++) {
              let selectOption = {
                workoutId: newWorkouts[i].workoutId,
                title: newWorkouts[i].title,
                lessonName: newWorkouts[i].lessonName,
                color: newWorkouts[i].lessonColor
              }

              newSelectOptions.push(selectOption);

              setWods(newWorkouts);
              setSelectOptions(newSelectOptions);
            }
          }
          else {
            setWods([]);
            setSelectOptions([]);
          }
        }
        setIsLoading(false);
      }
    )();
  }, [date])

  // 선택된 정보에 맞게 와드 데이터 세팅 //
  useEffect(() => {
    if (wods.length === 0 || selectedOption === undefined) {
      setSelectedWod(undefined);
      return;
    }

    let wodIndex = wods.findIndex(obj => obj.workoutId === selectedOption.workoutId);

    setSelectedWod(wods[wodIndex]);
  }, [wods, selectedOption])

  // 한자리 숫자 앞에 0 붙이기 //
  function numberPad(n, width) {
    n = n + '';
    return n.length >= width ? n : new Array(width - n.length + 1).join('0') + n;
  }

  // Section Title 설정 //
  function decideSectionTitle(data) {
    if (data === undefined) return '';
    else if (data.tag === 'ForTime') return data.round + ' Round For Time';
    else if (data.tag === 'AMRAP') {
      let min = parseInt(data.exerciseTime / 60);
      let sec = parseInt(data.exerciseTime % 60);

      if (min > 0 && sec > 0) return min + 'min ' + sec + 'sec AMRAP';
      else if (min > 0) return min + 'min AMRAP';
      else if (sec > 0) return sec + 'sec AMRAP';
      else return 'AMRAP';
    }
    else if (data.tag === 'EMOM') {
      let min = parseInt(data.round * data.exerciseTime / 60);
      let sec = parseInt(data.round * data.exerciseTime % 60);

      if (min > 0 && sec > 0) return min + 'min ' + sec + 'sec EMOM';
      else if (min > 0) return min + ' min EMOM';
      else if (sec > 0) return sec + ' sec EMOM';
      else return 'EMOM';
    }
    else if (data.tag === 'TABATA') {
      let onMin = parseInt(data.exerciseTime / 60);
      let onSec = parseInt(data.exerciseTime % 60);
      let offMin = parseInt(data.restTime / 60);
      let offSec = parseInt(data.restTime % 60);

      return 'On: ' + onMin + ':' + numberPad(onSec, 2) + ' / Off:' + offMin + ':' + numberPad(offSec, 2) + ' ' + data.round + 'R TABATA';
    }
    else if (data.tag === 'SINGLE') {
      if (data.mainExerciseList.length <= 0) return 'SINGLE';
      else if (data.mainExerciseList.length === 1) return data.mainExerciseList[0].movementName;
      else return 'Multiple Single';
    }
    else if (data.tag === 'Custom') {
      if (data.customTitle !== '') return data.customTitle;
      else if (data.scoreKeyword === '점수 없음'
        || data.scoreKeyword === '피크와트'
        || data.scoreKeyword === '최고 속도') return 'Custom : ' + data.scoreKeyword;
      else return 'Custom : ' + data.scoreKeyword + ' ' + data.scorePriority;
    }
  }

  // 보여줄 섹션 결정 //
  function decideViewSection() {
    if (nowScaleSection === undefined) return selectedWod.sectionList[nowSection];
    else return selectedWod.sectionList[nowSection].scaleSections[nowScaleSection];
  }

  // 칠판모드 버튼 호버링 시작 //
  function onMouseOverBlackboardButton() {
    setIsHoverBlackboardButton(true);
  }

  // 칠판모드 버튼 호버링 끝 //
  function onMouseLeaveBlackboardButton() {
    setIsHoverBlackboardButton(false);
  }

  // 메인 세션 클릭시 //
  function onClickNowSection(index) {
    setNowSection(index);
    setNowScaleSection(undefined);
  }

  // 스케일 세션 클릭시 //
  function onClickScaleSectionButton(index) {
    setNowScaleSection(index);
  }

  // 순위표 보기 클릭시 //
  function onClickRanking() {
    setIsClickedRanking(!isClickedRanking);
  }

  // 링크 클릭시 //
  function onClickURL(url) {
    window.open(url, "_blank", "noreferrer");
  }

  // 날짜 이전 버튼 클릭시 //
  function onClickPrevDate() {
    setDate(date.clone().subtract(1, 'days'));
  }

  // 날짜 다음 버튼 클릭시 //
  function onClickNextDate() {
    setDate(date.clone().add(1, 'days'));
  }

  // 칠판 모드 클릭시 //
  function onClickBlackboardModeButton() {
    let token = window.sessionStorage.getItem('Token').replaceAll('Bearer ', '');
    let dateParam = date.format('YYYY-MM-DD');

    window.open('https://link-zone.io/full/board/' + token + '?date=' + dateParam, '', '_self',);
    // window.open('http://localhost:3000/full/board/' + token + '?date=' + dateParam, '', '_self',);
  }

  return (
    <Container>
      <TitleWrapper>
        <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text} cursor='default'>{t('board.title')}</Text>
        <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subText} cursor='default'>{t('board.desc')}</Text>
      </TitleWrapper>
      <TopLineWrapper>
        <TopLineButtonLeftWrapper>
          <TopLineButtonWrapper>
            <DateButtonWrapper>
              <ArrowWrapper onClick={onClickPrevDate} >
                <ArrowIcon $isRight={false} />
              </ArrowWrapper>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>{date.clone().format(t('board.dateFormat'))}</Text>
              <ArrowWrapper onClick={onClickNextDate}>
                <ArrowIcon $isRight={true} />
              </ArrowWrapper>
            </DateButtonWrapper>
            <CalendarBoxButton
              width={38}
              height={38}
              iconWidth={18}
              iconHeight={18}
              value={date}
              setValue={setDate}
              isMainBoard={true} />
          </TopLineButtonWrapper>
          <DivisionLine width='2px' height='22px' radius={2} backgroundColor={theme.line} />
          <SelectBoxWrapper>
            <WorkoutSelectBox options={selectOptions} value={selectedOption} setValue={setSelectedOption} />
          </SelectBoxWrapper>
        </TopLineButtonLeftWrapper>
        <BlackBoardButton
          onMouseOver={onMouseOverBlackboardButton}
          onMouseLeave={onMouseLeaveBlackboardButton}
          onClick={onClickBlackboardModeButton}>
          <BlackBoardModeIcon />
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{t('board.blackboardMode')}</Text>
        </BlackBoardButton>
      </TopLineWrapper>

      <DivisionLine style={{ margin: '16px 0px 28px 0px' }} width='100%' height='1px' radius={2} backgroundColor={theme.line} />
      <InfoWrapper isDark={isDark} isClickedRanking={isClickedRanking} >
        <InfoInnerWrapper>
          {
            (isLoading || (wods.length > 0 && selectedWod === undefined)) ?
              null
              :
              selectedWod === undefined ?
                <TotalWodBox>
                  <NoneWodBox>
                    <EmptyImage />
                    <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.subText} cursor='default'>{t('board.noProgram')}</Text>
                  </NoneWodBox>
                </TotalWodBox>
                :
                <TotalWodBox>
                  <WodBoxWrapper isClickedRanking={isClickedRanking}>
                    <TotalWodSectionButtonWrapper>
                      <WodSectionButtonWrapper>
                        {
                          selectedWod.sectionList.map((data, index) => (
                            <WodSectionButton key={'MainBoardSection-' + index} isClicked={nowSection === index} onClick={() => onClickNowSection(index)}>
                              <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={nowSection === index ? theme.white : theme.subText} hover='pointer'>{String.fromCharCode(index + 65)}</Text>
                            </WodSectionButton>
                          ))
                        }
                      </WodSectionButtonWrapper>
                      <RankingButton isClickedRanking={isClickedRanking} onClick={onClickRanking}>
                        <Image width={18} height={20} src={WhiteCrownImage} />
                      </RankingButton>
                    </TotalWodSectionButtonWrapper>
                    <ScaleSectionBoxWrapper>
                      <WodSectionButtonWrapper>
                        {
                          selectedWod.sectionList[nowSection].scaleSections.map((data, index) => (
                            <ScaleWodSectionButton isClicked={nowScaleSection === index} onClick={() => onClickScaleSectionButton(index)}>
                              <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={nowScaleSection === index ? theme.subBackground : theme.subText} hover='pointer'>{String.fromCharCode(nowSection + 65) + '-' + (index + 2)}</Text>
                            </ScaleWodSectionButton>
                          ))
                        }
                      </WodSectionButtonWrapper>
                    </ScaleSectionBoxWrapper>
                    <SectionTitleWrapper>
                      <SectionBox isClickedScale={nowScaleSection !== undefined}>
                        <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={theme.white} hover='pointer'>{nowScaleSection === undefined ? String.fromCharCode(nowSection + 65) : String.fromCharCode(nowSection + 65) + '-' + (nowScaleSection + 2)}</Text>
                      </SectionBox>
                      <SectionTitleBox isClickedScale={nowScaleSection !== undefined}>
                        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='pointer'>{decideSectionTitle(decideViewSection())}</Text>
                      </SectionTitleBox>
                    </SectionTitleWrapper>
                    <MainPageWodViewBox
                      sectionData={decideViewSection()}
                      onClickURL={onClickURL}
                      isClickedRanking={isClickedRanking}
                      isMainPage={false} />
                  </WodBoxWrapper>
                  <RankingBoxWrapper isClickedRanking={isClickedRanking} $isDark={isDark} >
                    <MainPageRankingView
                      nowSection={nowSection}
                      setNowSection={setNowSection}
                      selectedWod={selectedWod}
                      isClickedRanking={isClickedRanking}
                      onClickGoBack={onClickRanking}/>
                  </RankingBoxWrapper>
                </TotalWodBox>
          }
        </InfoInnerWrapper>
      </InfoWrapper>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  
  padding-bottom: 180px;
  margin-left: 14px;

  width: 1035px;
  min-width: 1035px;
  min-height: 660px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;
`;

const TopLineWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  margin-top: 16px;

  width: 100%;
`;

const TopLineButtonLeftWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 20px;
`;

const TopLineButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;
`;

const BlackBoardButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0px 10px;

  box-sizing: border-box;
  height: 38px;

  border: 1px solid ${props => props.theme.buttonStroke};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};

  gap: 10px;

  &:hover {
    cursor: pointer;
    border: 1px solid ${props => props.theme.subText};
  }

  transition: all 0.3s ease-in-out;
`;

const DateButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  box-sizing: border-box;
  width: 243px;
  height: 38px;

  padding: 0 5px 0 5px;

  border: 1px solid ${props => props.theme.buttonStroke};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};

`;

const ArrowWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 22px;
  height: 22px;

  &:hover { 
    cursor: pointer;
  }
`;

const ArrowIcon = styled(LeftArrowSvg)`
  width: 9px;
  height: 15px;

  transform: rotate(${props => props.$isRight ? 180 : 0}deg);
  transition: all 0.3s ease-in-out;

  & path {
    fill: ${props => props.theme.line};
  }
`;

const DivisionLine = styled.div`
  width: ${props => props.width};
  height: ${props => props.height};

  border-radius: ${props => props.radius}px;
  background-color: ${props => props.backgroundColor};
`;

const SelectBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 200px;
  height: 38px;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  padding: ${props => props.isClickedRanking ? '0 0 0 20px' : '0 20px'};

  box-sizing: border-box;
  width: 100%;

  border-radius: 10px;
  background-color: ${props => props.isDark ? 'transparent' : props.theme.subBackground};

  transition: all 0.3s ease-in-out;
`;

const InfoInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 100%;
`;

const TotalWodBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: stretch;

  width: 100%;
`;

const WodBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: ${props => props.isClickedRanking ? '55%' : '100%'};
  
  transition: all 0.3s ease-in-out;
`;

const RankingBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  position: relative;

  margin-left: ${props => props.isClickedRanking ? '15px' : '0px'};

  width: ${props => props.isClickedRanking ? '443px' : '0%'};

  border-radius: 40px 40px 20px 20px;
  box-shadow: ${props => props.$isDark ? 'none' : '-20px 0px 20px rgba(0, 0, 0, 0.02)'};

  transition: all 0.3s ease-in-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 105px;
    border-radius: 20px 20px 0 0;
    box-shadow: ${props => props.$isDark ? 'none' : '-10px 10px 30px rgba(0, 0, 0, 0.05)'};
    clip-path: inset(-100px -100px 0 -100px);
    pointer-events: none;
  }
`;

const TotalWodSectionButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-item: center;

  padding: 20px 0px 6px 0px;

  width: 100%;
`;

const WodSectionButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-item: center;

  gap: 8px;
`;

const ScaleSectionBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-item: center;
  
  width: 100%;
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-item: center;

  margin-top: 20px;

  width: 100%;
`;

const SectionBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: ${props => props.isClickedScale ? `45px` : `34px`};
  height: 34px;

  border-radius: 6px;
  background-color: ${props => props.isClickedScale ? props.theme.category5 : props.theme.subKey};

  &:hover {
    cursor: default;
  }

  transition: all 0.3s ease-in-out;
`;

const SectionTitleBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: 0px 15px;

  width: ${props => props.isClickedScale ? `calc(100% - 81px)` : `calc(100% - 70px)`};
  height: 34px;

  border-radius: 6px;
  background-color: ${props => props.theme.buttonFill};

  transition: all 0.3s ease-in-out;
`;

const WodSectionButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 32px;
  height: 32px;

  border-radius: 6px;
  background-color: ${props => props.isClicked ? props.theme.subKey : props.theme.buttonFill};

  &:hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const ScaleWodSectionButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0 10px;
  
  height: 32px;

  border-radius: 6px;
  background-color: ${props => props.isClicked ? props.theme.category5 : props.theme.buttonFill};

  &:hover {
    cursor: pointer;
  }

  transition: all 0.3s ease-in-out;
`;

const RankingButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 63px;
  height: 32px;

  border-radius: 5px;
  background-color: ${props => props.theme.pointYellow};

  opacity: ${props => props.isClickedRanking ? 0 : 1};
  pointer-events: ${props => props.isClickedRanking ? 'none' : 'all'};

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
  }
`;

const NoneWodBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 60px 0px;

  width: 100%;

  gap: 8px;
`;


const BlackBoardModeIcon = styled(BlackBoardModeIconSvg)`
  width: 20px;
  height: 20px;

  flex-shrink: 0;

  & path {
    fill: ${props => props.theme.text};
    transition: fill 0.3s ease-in-out;
  }
`;

const Image = styled.img`
  width: ${props => props.width}px;
  height: ${props => props.height}px;

  transition: all 0.3s ease-in-out;
`;

const EmptyImage = styled(EmptySignSvg)`
  width: 148px;
  height: 62px;

  /* 배경 표지판 (root level rect) */
  & > rect {
    fill: ${props => props.theme.buttonFill3};
  }

  /* "EMPTY" 텍스트 */
  & > path {
    fill: ${props => props.theme.white};
  }

  /* 매다는 끈 2개 (g 안의 rects) */
  & > g rect {
    fill: ${props => props.theme.line};
  }
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
   
  &:hover {
    cursor: ${props => props.cursor};
  }

  transition: all 0.3s ease-in-out;
`;
